import { cx } from '@/lib/clases'
import { PuntoEstado } from '@/ui/InsigniaEstado'
import type { Tono } from '@/ui/tonos'

/**
 * Indicador del servicio de DGII, **siempre visible en la barra superior**.
 *
 * "Esto siempre visible es una decisión de producto, no decoración: cuando algo
 * falla, es lo primero que el usuario necesita saber, y le ahorra una llamada a
 * soporte."
 *
 * Cuatro estados, y el cuarto es el que se olvida: si el navegador no alcanza
 * nuestro servidor, el estado de DGII es **desconocido** y se dice. Un verde
 * rancio durante una caída es peor que no tener indicador.
 *
 * Y el contador de pendientes es también un contador de tiempo: "12 pendientes"
 * a secas no sirve, porque hay dos plazos legales corriendo.
 */

export type EstadoDGII = 'normal' | 'lenta' | 'caida' | 'sin-dato'

const TONO: Record<EstadoDGII, Tono> = {
  normal: 'verde',
  lenta: 'ambar',
  caida: 'rojo',
  'sin-dato': 'neutral',
}

export interface PropsIndicadorDGII {
  estado: EstadoDGII
  /** Documentos firmados esperando transmisión. */
  pendientes?: number
  /** Horas del más viejo, contra el plazo de 72. */
  horasDelMasViejo?: number
  /** Días en contingencia, contra el máximo de 15. */
  diasEnContingencia?: number
  onClick?: () => void
  className?: string
}

export function IndicadorDGII({
  estado,
  pendientes,
  horasDelMasViejo,
  diasEnContingencia,
  onClick,
  className,
}: PropsIndicadorDGII) {
  const partes: string[] = ['DGII']

  if (estado === 'lenta') partes.push('lenta')
  if (estado === 'sin-dato') partes.push('sin dato')

  if (estado === 'caida') {
    partes.push('caída')
    if (pendientes !== undefined) partes.push(`${pendientes} pendientes`)
    if (horasDelMasViejo !== undefined) partes.push(`el más viejo ${horasDelMasViejo} h de 72`)
  }

  const contingencia =
    diasEnContingencia !== undefined ? `Contingencia activa hace ${diasEnContingencia} d de 15` : null

  return (
    <button
      type="button"
      onClick={onClick}
      title={contingencia ?? undefined}
      className={cx(
        'flex h-[var(--alto-control)] items-center gap-2 rounded-md px-2',
        'text-cuerpo text-gris-900 hover:bg-gris-100',
        className,
      )}
    >
      <PuntoEstado tono={TONO[estado]} />
      {/* Punto de color MÁS texto. El punto nunca va solo. */}
      <span className="whitespace-nowrap">{partes.join(' · ')}</span>
      {contingencia ? (
        <span className="hidden whitespace-nowrap text-etiqueta text-ambar lg:inline">{contingencia}</span>
      ) : null}
    </button>
  )
}
